import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import api from '../utils/api';
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2, X, Settings, Printer as PrinterIcon, Calendar } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { getMonthGrid, getWeekBars, buildContinuousWeeks } from '../utils/calendarGrid';
import { getContrastColor } from '../utils/color';

function koreaToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
}

const PRESET_COLORS = ['#818cf8', '#f87171', '#4ade80', '#fb923c', '#22d3ee', '#facc15', '#e879f9', '#f472b6', '#a78bfa', '#34d399'];
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function addDays(dateStr, days) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

// ── 프린트 뷰 (연속 캘린더) ────────────────────────────────────
const BAR_H = 18;
const DAY_H = 24;

function PrintView({ config, schedules, printers }) {
  const todayStr = koreaToday();
  const weeks = buildContinuousWeeks(config.fromYear, config.fromMonth, config.toYear, config.toMonth);

  const title = config.fromYear === config.toYear && config.fromMonth === config.toMonth
    ? `${config.fromYear}년 ${config.fromMonth + 1}월`
    : `${config.fromYear}년 ${config.fromMonth + 1}월 ~ ${config.toYear}년 ${config.toMonth + 1}월`;

  return (
    <div style={{ fontFamily: 'sans-serif', background: '#fff', padding: 16 }}>
      {/* 제목 + 범례 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: '#111', margin: 0 }}>{title}</h2>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {printers.map(p => (
            <span key={p.id} style={{ fontSize: 9, display: 'flex', alignItems: 'center', gap: 3 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: p.color, display: 'inline-block', flexShrink: 0 }} />
              {p.name}
            </span>
          ))}
        </div>
      </div>

      {/* 요일 헤더 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '2px solid #333', borderTop: '1px solid #ddd' }}>
        {WEEKDAYS.map((d, i) => (
          <div key={d} style={{ textAlign: 'center', padding: '3px 0', fontSize: 9, fontWeight: 700,
            color: i === 0 ? '#c53030' : i === 6 ? '#2b6cb0' : '#444' }}>{d}</div>
        ))}
      </div>

      {/* 연속 주 */}
      {weeks.map((weekCells, wi) => {
        const barCells = weekCells.map(c => c.inRange ? c : null);
        const bars = getWeekBars(barCells, schedules, 'schedule');
        const maxRow = bars.reduce((m, b) => Math.max(m, b.row), -1) + 1;
        const rowH = Math.max(48, DAY_H + maxRow * (BAR_H + 2) + 4);

        return (
          <div key={wi} style={{ position: 'relative', minHeight: rowH, borderBottom: '1px solid #ddd' }}>
            {/* 배경 */}
            <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {weekCells.map((cell, di) => (
                <div key={di} style={{
                  borderRight: di < 6 ? '1px solid #eee' : 'none',
                  borderLeft: cell.isNewMonth && cell.inRange && di > 0 ? '2px solid #818cf8' : 'none',
                  backgroundColor: !cell.inRange ? '#f3f3f3' : '#fff',
                }} />
              ))}
            </div>

            {/* 날짜 숫자 */}
            <div style={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {weekCells.map((cell, di) => {
                const isToday = cell.dateStr === todayStr;
                const isMonthStart = cell.isNewMonth && cell.inRange;
                return (
                  <div key={di} style={{ padding: '2px 3px', borderLeft: isMonthStart && di > 0 ? '2px solid #818cf8' : 'none' }}>
                    {isMonthStart && (
                      <div style={{ fontSize: 7, fontWeight: 700, color: '#4338ca', lineHeight: 1, marginBottom: 1 }}>
                        {cell.month + 1}월
                      </div>
                    )}
                    <span style={{
                      fontSize: 10, fontWeight: cell.inRange ? 600 : 400,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 16, height: 16, borderRadius: '50%',
                      color: !cell.inRange ? '#bbb'
                        : isToday ? '#fff'
                        : di === 0 ? '#c53030'
                        : di === 6 ? '#2b6cb0'
                        : '#222',
                      backgroundColor: isToday ? '#667eea' : 'transparent',
                    }}>
                      {cell.day}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* 일정 바 */}
            <div style={{ position: 'relative', zIndex: 2, marginTop: 1 }}>
              {Array.from({ length: maxRow }, (_, ri) => (
                <div key={ri} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', height: BAR_H, marginBottom: 2 }}>
                  {bars.filter(b => b.row === ri).map(b => {
                    const color = b.schedule.printer?.color || '#818cf8';
                    const pName = b.schedule.printer?.name || '';
                    return (
                      <div key={b.schedule.id} style={{
                        gridColumn: `${b.startCol + 1} / span ${b.span}`,
                        backgroundColor: color, color: getContrastColor(color), fontSize: 8,
                        padding: '1px 3px', display: 'flex', alignItems: 'center', gap: 2,
                        overflow: 'hidden', whiteSpace: 'nowrap', margin: '0 1px',
                        borderRadius: b.isStart && b.isEnd ? 3 : b.isStart ? '3px 0 0 3px' : b.isEnd ? '0 3px 3px 0' : 0,
                      }}>
                        {pName && (
                          <span style={{ backgroundColor: 'rgba(0,0,0,0.25)', borderRadius: 2, padding: '0 2px', flexShrink: 0, fontSize: 7 }}>
                            {pName}
                          </span>
                        )}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.schedule.title}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PrintConfigModal({ defaultYear, defaultMonth, onConfirm, onClose }) {
  const [fromYear, setFromYear] = useState(defaultYear);
  const [fromMonth, setFromMonth] = useState(defaultMonth);
  const [toYear, setToYear] = useState(defaultYear);
  const [toMonth, setToMonth] = useState(defaultMonth);
  const months = Array.from({ length: 12 }, (_, i) => i);
  const years = Array.from({ length: 5 }, (_, i) => defaultYear - 1 + i);
  const isValid = toYear > fromYear || (toYear === fromYear && toMonth >= fromMonth);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 w-full max-w-xs overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold text-sm flex items-center gap-2"><PrinterIcon size={14} /> 캘린더 프린트</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-gray-400 text-xs mb-1 block">시작 월</label>
            <div className="flex gap-2">
              <select value={fromYear} onChange={e => setFromYear(parseInt(e.target.value))}
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white text-sm focus:outline-none">
                {years.map(y => <option key={y} value={y}>{y}년</option>)}
              </select>
              <select value={fromMonth} onChange={e => setFromMonth(parseInt(e.target.value))}
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white text-sm focus:outline-none">
                {months.map(m => <option key={m} value={m}>{m + 1}월</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">종료 월</label>
            <div className="flex gap-2">
              <select value={toYear} onChange={e => setToYear(parseInt(e.target.value))}
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white text-sm focus:outline-none">
                {years.map(y => <option key={y} value={y}>{y}년</option>)}
              </select>
              <select value={toMonth} onChange={e => setToMonth(parseInt(e.target.value))}
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white text-sm focus:outline-none">
                {months.map(m => <option key={m} value={m}>{m + 1}월</option>)}
              </select>
            </div>
          </div>
          {!isValid && <p className="text-red-400 text-xs">종료 월이 시작 월보다 앞입니다.</p>}
        </div>
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-gray-400 text-sm hover:text-white">취소</button>
          <button onClick={() => isValid && onConfirm({ fromYear, fromMonth, toYear, toMonth })} disabled={!isValid}
            className="px-4 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80 disabled:opacity-40 flex items-center gap-1.5">
            <PrinterIcon size={13} /> 프린트
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 메인 ──────────────────────────────────────────────────────
export default function CalendarPage() {
  const today = koreaToday();
  const [year, setYear] = useState(() => parseInt(today.split('-')[0]));
  const [month, setMonth] = useState(() => parseInt(today.split('-')[1]) - 1);
  const [printers, setPrinters] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [showPrinterMgr, setShowPrinterMgr] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [form, setForm] = useState({ printerId: '', title: '', startDate: '', endDate: '' });
  const [printerForm, setPrinterForm] = useState({ name: '', color: '#818cf8' });
  const [editingPrinter, setEditingPrinter] = useState(null);

  const [printConfig, setPrintConfig] = useState(null);
  const [printSchedules, setPrintSchedules] = useState([]);
  const printPending = useRef(false);

  const grid = useMemo(() => getMonthGrid(year, month), [year, month]);
  const weeks = useMemo(() => { const r = []; for (let i = 0; i < grid.length; i += 7) r.push(grid.slice(i, i + 7)); return r; }, [grid]);

  const loadPrinters = useCallback(() => api.get('/calendar/printers').then(r => setPrinters(r.data)), []);
  const loadSchedules = useCallback(() => {
    const start = grid[0].dateStr;
    const end = grid[grid.length - 1].dateStr;
    api.get('/calendar/schedules', { params: { start, end } }).then(r => setSchedules(r.data));
  }, [grid]);

  useEffect(() => { loadPrinters(); }, [loadPrinters]);
  useEffect(() => { loadSchedules(); }, [loadSchedules]);
  useEffect(() => {
    const iv = setInterval(() => { loadSchedules(); loadPrinters(); }, 5000);
    return () => clearInterval(iv);
  }, [loadSchedules, loadPrinters]);

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };
  const goThisMonth = () => { const t = today.split('-'); setYear(parseInt(t[0])); setMonth(parseInt(t[1]) - 1); };

  const weekBarsMap = useMemo(() => weeks.map(w => getWeekBars(w, schedules, 'schedule')), [weeks, schedules]);

  // 드래그앤 드롭
  const dragRef = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);

  const handleBarDragStart = (e, schedule) => {
    dragRef.current = schedule;
    setDraggingId(schedule.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleBarDragEnd = () => {
    setDraggingId(null);
    setDragOverDate(null);
    dragRef.current = null;
  };
  const handleCellDrop = async (e, cell) => {
    e.preventDefault();
    setDragOverDate(null);
    const schedule = dragRef.current;
    if (!schedule) return;
    dragRef.current = null;
    setDraggingId(null);
    const days = Math.round((new Date(cell.dateStr + 'T00:00:00') - new Date(schedule.startDate + 'T00:00:00')) / 86400000);
    if (days === 0) return;
    const newStart = addDays(schedule.startDate, days);
    const newEnd = addDays(schedule.endDate, days);
    setSchedules(prev => prev.map(s => s.id === schedule.id ? { ...s, startDate: newStart, endDate: newEnd } : s));
    try {
      await api.put(`/calendar/schedules/${schedule.id}`, { printerId: schedule.printerId, title: schedule.title, startDate: newStart, endDate: newEnd });
    } catch {
      loadSchedules();
    }
  };

  const openAddSchedule = (cell) => {
    if (!cell.isCurrentMonth) return;
    setEditingSchedule(null);
    setForm({ printerId: printers[0]?.id || '', title: '', startDate: cell.dateStr, endDate: cell.dateStr });
    setShowForm(true);
  };
  const openEditSchedule = (s) => {
    setEditingSchedule(s);
    setForm({ printerId: s.printerId, title: s.title, startDate: s.startDate, endDate: s.endDate });
    setShowForm(true);
  };
  const saveSchedule = async () => {
    if (!form.title.trim() || !form.printerId) return;
    if (editingSchedule) await api.put(`/calendar/schedules/${editingSchedule.id}`, form);
    else await api.post('/calendar/schedules', form);
    setShowForm(false);
    loadSchedules();
  };
  const deleteSchedule = async (id) => {
    await api.delete(`/calendar/schedules/${id}`);
    setShowForm(false);
    loadSchedules();
  };
  const savePrinter = async () => {
    if (!printerForm.name.trim()) return;
    if (editingPrinter) await api.put(`/calendar/printers/${editingPrinter.id}`, printerForm);
    else await api.post('/calendar/printers', printerForm);
    setEditingPrinter(null);
    setPrinterForm({ name: '', color: '#818cf8' });
    loadPrinters(); loadSchedules();
  };
  const deletePrinter = async (id) => {
    if (!confirm('이 프린터와 관련 스케줄이 모두 삭제됩니다.')) return;
    await api.delete(`/calendar/printers/${id}`);
    loadPrinters(); loadSchedules();
  };

  // 프린트
  const initiatePrint = async (cfg) => {
    const startDate = `${cfg.fromYear}-${String(cfg.fromMonth + 1).padStart(2, '0')}-01`;
    const toLastDay = new Date(cfg.toYear, cfg.toMonth + 1, 0).getDate();
    const endDate = `${cfg.toYear}-${String(cfg.toMonth + 1).padStart(2, '0')}-${String(toLastDay).padStart(2, '0')}`;
    const r = await api.get('/calendar/schedules', { params: { start: startDate, end: endDate } });
    setPrintSchedules(r.data);
    setPrintConfig(cfg);
    printPending.current = true;
    setShowPrintModal(false);
  };

  useEffect(() => {
    if (!printConfig || !printPending.current) return;
    printPending.current = false;
    const t = setTimeout(() => {
      window.print();
      const reset = () => setPrintConfig(null);
      window.addEventListener('afterprint', reset, { once: true });
    }, 150);
    return () => clearTimeout(t);
  }, [printConfig, printSchedules]);

  const isThisMonth = year === parseInt(today.split('-')[0]) && month === parseInt(today.split('-')[1]) - 1;

  return (
    <>
      <div className="cal-print-only">
        {printConfig && <PrintView config={printConfig} schedules={printSchedules} printers={printers} />}
      </div>

      <div className="cal-no-print flex flex-col flex-1 min-h-0">
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="flex items-center gap-1">
            <Calendar size={20} className="text-brand flex-shrink-0 mr-1" />
            <button onClick={prevMonth} className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-800 transition-colors"><ChevronLeft size={20} /></button>
            <h1 className="text-xl font-bold text-white min-w-[140px] text-center">{year}년 {month + 1}월</h1>
            <button onClick={nextMonth} className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-800 transition-colors"><ChevronRight size={20} /></button>
            {isThisMonth ? (
              <span className="px-1.5 py-0.5 text-[10px] text-brand bg-brand/10 rounded font-medium whitespace-nowrap ml-1">이번 달</span>
            ) : (
              <button onClick={goThisMonth} className="px-2 py-1 text-xs text-brand border border-brand/30 rounded hover:bg-brand/10 whitespace-nowrap ml-1">이번 달로</button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {printers.length > 0 && (
              <div className="hidden sm:flex items-center gap-2 overflow-x-auto scrollbar-none min-w-0">
                {printers.map(p => (
                  <div key={p.id} className="flex items-center gap-1 flex-shrink-0">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: p.color }} />
                    <span className="text-gray-400 text-xs whitespace-nowrap">{p.name}</span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => setShowPrintModal(true)} title="프린트"
              className="flex items-center gap-1 p-1.5 text-gray-400 hover:text-white text-xs rounded-lg hover:bg-gray-800 border border-gray-800">
              <PrinterIcon size={13} /><span className="hidden sm:inline">프린트</span>
            </button>
            <button onClick={() => setShowPrinterMgr(true)} title="프린터 관리"
              className="flex items-center gap-1 p-1.5 text-gray-400 hover:text-white text-xs rounded-lg hover:bg-gray-800 border border-gray-800">
              <Settings size={13} /><span className="hidden sm:inline">프린터 관리</span>
            </button>
          </div>
        </div>

        {/* 요일 헤더 */}
        <div className="grid grid-cols-7 border-b border-gray-800 flex-shrink-0">
          {WEEKDAYS.map((d, i) => (
            <div key={d} className={`text-center py-2.5 text-sm font-semibold ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-500'}`}>{d}</div>
          ))}
        </div>

        {/* 캘린더 */}
        {(() => {
          const frVals = weeks.map((_, wi) => {
            const maxRow = weekBarsMap[wi].reduce((m, b) => Math.max(m, b.row), -1) + 1;
            return Math.max(80, 44 + maxRow * 26);
          });
          const totalFr = frVals.reduce((s, v) => s + v, 0);
          return (
        <div className="flex-1 min-h-0" style={{ display: 'grid', gridTemplateRows: frVals.map(v => `minmax(0, ${v}fr)`).join(' ') }}>
          {weeks.map((weekCells, wi) => {
            const bars = weekBarsMap[wi];
            const maxRow = bars.reduce((m, b) => Math.max(m, b.row), -1) + 1;
            return (
              <div key={wi} style={{ overflow: 'hidden' }} className="border-b border-gray-800/50 relative">
                <div className="absolute inset-0 grid grid-cols-7">
                  {weekCells.map((cell, di) => (
                    <div key={di}
                      className={`${di < 6 ? 'border-r border-gray-800/30' : ''} ${cell.isCurrentMonth ? 'hover:bg-gray-800/10 cursor-pointer group' : 'bg-gray-950/30'} ${dragOverDate === cell.dateStr ? 'bg-brand/20' : ''}`}
                      onDoubleClick={() => openAddSchedule(cell)}
                      onDragOver={e => { e.preventDefault(); setDragOverDate(cell.dateStr); }}
                      onDrop={e => handleCellDrop(e, cell)}
                    >
                      {cell.isCurrentMonth && (
                        <div className="flex justify-end p-0.5">
                          <button onClick={e => { e.stopPropagation(); openAddSchedule(cell); }}
                            className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-600 hover:text-brand rounded transition-opacity">
                            <Plus size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="relative z-10 grid grid-cols-7 pointer-events-none">
                  {weekCells.map((cell, di) => {
                    const isToday = cell.dateStr === today;
                    return (
                      <div key={di} className="px-1.5 pt-1 pb-0.5">
                        <span className={`text-sm font-semibold inline-flex items-center justify-center w-7 h-7 rounded-full ${
                          isToday ? 'bg-brand text-white'
                            : !cell.isCurrentMonth ? 'text-gray-700'
                            : di === 0 ? 'text-red-400'
                            : di === 6 ? 'text-blue-400'
                            : 'text-gray-300'
                        }`}>{cell.day}</span>
                      </div>
                    );
                  })}
                </div>

                <div className="relative z-10 mt-0.5">
                  {Array.from({ length: maxRow }, (_, ri) => (
                    <div key={ri} className="grid grid-cols-7" style={{ height: '26px' }}>
                      {bars.filter(b => b.row === ri).map(b => {
                        const color = b.schedule.printer?.color || '#818cf8';
                        const pName = b.schedule.printer?.name || '';
                        return (
                          <div key={b.schedule.id}
                            draggable
                            onDragStart={e => handleBarDragStart(e, b.schedule)}
                            onDragEnd={handleBarDragEnd}
                            className={`h-[23px] mx-[1px] flex items-center text-[11px] text-white font-medium truncate cursor-grab active:cursor-grabbing hover:brightness-125 transition-all select-none ${b.isStart && b.isEnd ? 'rounded-md' : b.isStart ? 'rounded-l-md' : b.isEnd ? 'rounded-r-md' : ''} ${draggingId === b.schedule.id ? 'opacity-40' : ''}`}
                            style={{ gridColumn: `${b.startCol + 1} / span ${b.span}`, backgroundColor: color }}
                            onClick={() => openEditSchedule(b.schedule)}
                          >
                            {pName && <span className="bg-black/25 text-[10px] leading-none px-1 py-[2px] rounded flex-shrink-0 ml-[3px] font-semibold">{pName}</span>}
                            <span className="ml-1.5 truncate">{b.schedule.title}</span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
          );
        })()}

        {/* 스케줄 추가/수정 모달 */}
        {showForm && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-bold text-sm">{editingSchedule ? '스케줄 수정' : '스케줄 추가'}</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-white"><X size={16} /></button>
              </div>
              <div className="space-y-3 w-full overflow-x-hidden">
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">프린터</label>
                  <select value={form.printerId} onChange={e => setForm(f => ({ ...f, printerId: parseInt(e.target.value) }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand">
                    {printers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">출력 내용</label>
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="출력할 내용"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
                </div>
                <div className="flex flex-col gap-2">
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">시작일</label>
                    <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">종료일</label>
                    <input type="date" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-5">
                {editingSchedule ? (
                  <button onClick={() => deleteSchedule(editingSchedule.id)} className="text-red-400 text-xs hover:text-red-300">삭제</button>
                ) : <div />}
                <div className="flex gap-2">
                  <button onClick={() => setShowForm(false)} className="px-3 py-1.5 text-gray-400 text-sm hover:text-white">취소</button>
                  <button onClick={saveSchedule} className="px-4 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">저장</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 프린터 관리 모달 */}
        {showPrinterMgr && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowPrinterMgr(false)}>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-bold text-sm">프린터 관리</h2>
                <button onClick={() => setShowPrinterMgr(false)} className="text-gray-500 hover:text-white"><X size={16} /></button>
              </div>
              <div className="space-y-2 mb-4">
                {printers.map(p => (
                  <div key={p.id} className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
                    <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: p.color }} />
                    <span className="text-white text-sm flex-1">{p.name}</span>
                    <button onClick={() => { setEditingPrinter(p); setPrinterForm({ name: p.name, color: p.color }); }}
                      className="text-gray-500 hover:text-white p-1"><Pencil size={12} /></button>
                    <button onClick={() => deletePrinter(p.id)} className="text-gray-500 hover:text-red-400 p-1"><Trash2 size={12} /></button>
                  </div>
                ))}
                {printers.length === 0 && <p className="text-gray-600 text-xs text-center py-2">등록된 프린터가 없습니다.</p>}
              </div>
              <div className="border-t border-gray-800 pt-3">
                <p className="text-gray-400 text-xs mb-2">{editingPrinter ? '프린터 수정' : '프린터 추가'}</p>
                <input value={printerForm.name} onChange={e => setPrinterForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="프린터 이름" className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-brand mb-2" />
                <div className="flex items-center gap-1.5 mb-3">
                  {PRESET_COLORS.map(c => (
                    <button key={c} onClick={() => setPrinterForm(f => ({ ...f, color: c }))}
                      className={`w-5 h-5 rounded-full border-2 transition-transform ${printerForm.color === c ? 'border-white scale-125' : 'border-transparent hover:scale-110'}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
                <div className="flex gap-2">
                  {editingPrinter && (
                    <button onClick={() => { setEditingPrinter(null); setPrinterForm({ name: '', color: '#818cf8' }); }}
                      className="px-3 py-1.5 text-gray-400 text-sm hover:text-white">취소</button>
                  )}
                  <button onClick={savePrinter} className="px-4 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">
                    {editingPrinter ? '수정' : '추가'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showPrintModal && (
          <PrintConfigModal
            defaultYear={year} defaultMonth={month}
            onConfirm={initiatePrint}
            onClose={() => setShowPrintModal(false)}
          />
        )}
      </div>
    </>
  );
}
